import { useState, useEffect, useCallback, Fragment } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Package, Warehouse, FileText, Layers, Tag, Box, ArrowUpDown, Weight, Loader2, ChevronDown, ChevronRight, Search, X, Lock, CheckCircle2, Calendar, Edit3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import api from '@/lib/api';
import MarkBoqEditorModal from './MarkBoqEditorModal';

const STATUS_COLORS = {
  UNALLOCATED: { bg: '#ef444420', text: '#ef4444', label: 'Unallocated' },
  PARTIAL_SOFT: { bg: '#eab30820', text: '#eab308', label: 'Partial Soft' },
  READY_SOFT: { bg: '#3b82f620', text: '#3b82f6', label: 'Ready Soft' },
  PO_READY: { bg: '#143A6B20', text: '#143A6B', label: 'PO Ready' },
  PARTIAL_HARD: { bg: '#f59e0b20', text: '#f59e0b', label: 'Partial Hard' },
  FULLY_ALLOCATED: { bg: '#22c55e20', text: '#22c55e', label: 'Fully Allocated' },
};

const FAB_STAGES = ['cuttingAndFitting', 'welding', 'surfaceTreatment', 'dispatch'];
const FAB_LABELS = { cuttingAndFitting: 'Cut&Fit', welding: 'Welding', surfaceTreatment: 'Surface', dispatch: 'Dispatch' };
const FAB_COLORS = { cuttingAndFitting: '#3b82f6', welding: '#f59e0b', surfaceTreatment: '#143A6B', dispatch: '#22c55e' };
const EREC_STAGES = ['erection', 'boltTorquing', 'alignment', 'grouting'];
const EREC_LABELS = { erection: 'Erec', boltTorquing: 'BoltTorq', alignment: 'Align', grouting: 'Grout' };
const EREC_COLORS = { erection: '#3b82f6', boltTorquing: '#f59e0b', alignment: '#143A6B', grouting: '#22c55e' };

function fmtNum(num) { return num != null ? new Intl.NumberFormat('en-IN', { maximumFractionDigits: 1 }).format(num) : '-'; }
function fmtPct(v) { return v != null ? `${Number(v).toFixed(0)}%` : '0%'; }
function fmtDate(d) { return d ? String(d).slice(0, 10) : '-'; }

// ═══════════════════════════════════════════════════════
// Mark Number Detail Panel (expandable under each tag)
// ═══════════════════════════════════════════════════════

function MarkDetailPanel({ projectId, structuralTag, onClose }) {
  const [marks, setMarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('marks');
  const [editingMark, setEditingMark] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/v1/projects/${projectId}/swm/mark-numbers?structuralTag=${structuralTag}`);
      setMarks(res.data?.markNumbers || []);
    } catch { /* ignore */ }
    setLoading(false);
  }, [projectId, structuralTag]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return (
    <tr><td colSpan={16} className="p-4">
      <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--text-muted)' }} /></div>
    </td></tr>
  );

  const tabs = [
    { id: 'marks', label: `Mark Numbers (${marks.length})`, icon: Box },
    { id: 'fab', label: 'Fab Stage Gates', icon: Layers },
    { id: 'erec', label: 'Erection Stage Gates', icon: Package },
    { id: 'material', label: 'Material Requirements', icon: FileText },
  ];

  return (
    <tr data-testid={`detail-panel-${structuralTag}`}>
      <td colSpan={16} style={{ padding: 0 }}>
        <div className="mx-4 my-2 rounded-lg border overflow-hidden" style={{ background: 'var(--card-bg)', borderColor: '#F5920640' }}>
          {/* Sub-tab header */}
          <div className="flex items-center gap-1 px-3 py-2 border-b" style={{ background: '#F5920608', borderColor: '#F5920620' }}>
            {tabs.map(t => {
              const Icon = t.icon;
              return (
                <button key={t.id} onClick={() => setTab(t.id)} data-testid={`detail-tab-${t.id}-${structuralTag}`}
                  className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors"
                  style={{ background: tab === t.id ? '#F59206' : 'transparent', color: tab === t.id ? '#fff' : 'var(--text-muted)' }}>
                  <Icon className="w-3 h-3" /> {t.label}
                </button>
              );
            })}
            <div className="flex-1" />
            <button onClick={onClose} className="p-1 rounded hover:bg-black/10" data-testid={`close-detail-${structuralTag}`}>
              <X className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
            </button>
          </div>

          {/* Mark Numbers Tab */}
          {tab === 'marks' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    {['Mark #', 'Section', 'Pcs', 'Weight (kg)', 'BoQ', 'Alloc %', 'Fab Blocked', 'Fab Stage', 'Fab %', 'Erec %', 'Status'].map(h => (
                      <th key={h} className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {marks.map(m => {
                    const allocPct = m.overallAllocatedPercent || 0;
                    const boqCount = (m.materialRequirements || []).length;
                    return (
                      <tr key={m.markNumber} style={{ borderTop: '1px solid var(--border-color)' }} data-testid={`mark-row-${m.markNumber}`}>
                        <td className="p-1.5 font-mono font-semibold" style={{ color: '#F59206' }}>{m.markNumber}</td>
                        <td className="p-1.5" style={{ color: 'var(--text-muted)' }}>{m.section}</td>
                        <td className="p-1.5 text-center" style={{ color: 'var(--text-primary)' }}>{m.pieceCount}</td>
                        <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(m.totalWeight)}</td>
                        <td className="p-1.5 text-center">
                          <button onClick={() => setEditingMark(m)}
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-semibold hover:brightness-110 transition"
                            style={{ background: boqCount > 0 ? '#0EA5E920' : '#EF444420',
                                      color: boqCount > 0 ? '#0EA5E9' : '#EF4444',
                                      border: `1px solid ${boqCount > 0 ? '#0EA5E940' : '#EF444440'}` }}
                            title={boqCount > 0 ? `${boqCount} materials — edit` : 'No BoQ yet — click to define'}
                            data-testid={`edit-boq-${m.markNumber}`}>
                            <Edit3 className="w-2.5 h-2.5" />
                            {boqCount > 0 ? `${boqCount} mat` : 'Add BoQ'}
                          </button>
                        </td>
                        <td className="p-1.5 text-center">
                          <PctBadge value={allocPct} />
                        </td>
                        <td className="p-1.5 text-center">
                          {m.fabricationBlocked ? <Lock className="w-3 h-3 inline" style={{ color: '#ef4444' }} /> : <CheckCircle2 className="w-3 h-3 inline" style={{ color: '#22c55e' }} />}
                        </td>
                        <td className="p-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>{m.currentFabStage?.replace(/([A-Z])/g, ' $1').trim() || 'None'}</td>
                        <td className="p-1.5 text-center font-mono" style={{ color: '#143A6B' }}>{fmtPct(m.fabricationProgressPct)}</td>
                        <td className="p-1.5 text-center font-mono" style={{ color: '#f59e0b' }}>{fmtPct(m.erectionProgressPct)}</td>
                        <td className="p-1.5 text-center text-[9px]" style={{ color: allocPct >= 100 ? '#22c55e' : allocPct > 0 ? '#f59e0b' : '#ef4444' }}>
                          {allocPct >= 100 ? 'Full' : allocPct > 0 ? 'Partial' : 'None'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {editingMark && (
                <MarkBoqEditorModal projectId={projectId} mark={editingMark}
                  onClose={() => setEditingMark(null)}
                  onSaved={reload} />
              )}
            </div>
          )}

          {/* Fab Stage Gates Tab */}
          {tab === 'fab' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Mark #</th>
                    <th className="p-1.5 text-right font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Weight</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Alloc %</th>
                    {FAB_STAGES.map(s => (
                      <th key={s} className="p-1.5 text-center font-medium sticky top-0" style={{ color: FAB_COLORS[s], background: 'var(--card-bg-alt)', borderLeft: s === 'cuttingAndFitting' ? '2px solid var(--border-color)' : undefined }}>{FAB_LABELS[s]}</th>
                    ))}
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)', borderLeft: '2px solid var(--border-color)' }}>Fab %</th>
                  </tr>
                </thead>
                <tbody>
                  {marks.map(m => {
                    const fab = m.stageGates?.fabrication || {};
                    const allocPct = m.overallAllocatedPercent || 0;
                    const gateDate = (stage) => {
                      if (allocPct < 100) return <Lock className="w-3 h-3 inline" style={{ color: '#ef4444' }} />;
                      if (fab[stage]?.isComplete) return <span style={{ color: '#22c55e' }}>{fmtDate(fab[stage]?.date).slice(5)}</span>;
                      const idx = FAB_STAGES.indexOf(stage);
                      if (idx > 0 && !fab[FAB_STAGES[idx - 1]]?.isComplete) return <Lock className="w-3 h-3 inline" style={{ color: '#94a3b8' }} />;
                      return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
                    };
                    return (
                      <tr key={m.markNumber} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td className="p-1.5 font-mono font-semibold" style={{ color: '#F59206' }}>{m.markNumber}</td>
                        <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(m.totalWeight)}</td>
                        <td className="p-1.5 text-center"><PctBadge value={allocPct} /></td>
                        {FAB_STAGES.map(s => (
                          <td key={s} className="p-1.5 text-center" style={{ borderLeft: s === 'cuttingAndFitting' ? '2px solid var(--border-color)' : undefined }}>{gateDate(s)}</td>
                        ))}
                        <td className="p-1.5 text-center font-mono font-bold" style={{ color: '#143A6B', borderLeft: '2px solid var(--border-color)' }}>{fmtPct(m.fabricationProgressPct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Erection Stage Gates Tab */}
          {tab === 'erec' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    <th className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Mark #</th>
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>Fab Done</th>
                    {EREC_STAGES.map(s => (
                      <th key={s} className="p-1.5 text-center font-medium sticky top-0" style={{ color: EREC_COLORS[s], background: 'var(--card-bg-alt)', borderLeft: s === 'erection' ? '2px solid var(--border-color)' : undefined }}>{EREC_LABELS[s]}</th>
                    ))}
                    <th className="p-1.5 text-center font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)', borderLeft: '2px solid var(--border-color)' }}>Erec %</th>
                  </tr>
                </thead>
                <tbody>
                  {marks.map(m => {
                    const fab = m.stageGates?.fabrication || {};
                    const erec = m.stageGates?.erection || {};
                    const fabDone = fab.dispatch?.isComplete;
                    const gateDate = (stage) => {
                      if (!fabDone && stage === 'erection') return <Lock className="w-3 h-3 inline" style={{ color: '#ef4444' }} />;
                      if (erec[stage]?.isComplete) return <span style={{ color: '#22c55e' }}>{fmtDate(erec[stage]?.date).slice(5)}</span>;
                      const idx = EREC_STAGES.indexOf(stage);
                      if (idx > 0 && !erec[EREC_STAGES[idx - 1]]?.isComplete) return <Lock className="w-3 h-3 inline" style={{ color: '#94a3b8' }} />;
                      return <span style={{ color: 'var(--text-disabled)' }}>-</span>;
                    };
                    return (
                      <tr key={m.markNumber} style={{ borderTop: '1px solid var(--border-color)' }}>
                        <td className="p-1.5 font-mono font-semibold" style={{ color: '#F59206' }}>{m.markNumber}</td>
                        <td className="p-1.5 text-center">{fabDone ? <CheckCircle2 className="w-3 h-3 inline" style={{ color: '#22c55e' }} /> : <Lock className="w-3 h-3 inline" style={{ color: '#ef4444' }} />}</td>
                        {EREC_STAGES.map(s => (
                          <td key={s} className="p-1.5 text-center" style={{ borderLeft: s === 'erection' ? '2px solid var(--border-color)' : undefined }}>{gateDate(s)}</td>
                        ))}
                        <td className="p-1.5 text-center font-mono font-bold" style={{ color: '#f59e0b', borderLeft: '2px solid var(--border-color)' }}>{fmtPct(m.erectionProgressPct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Material Requirements Tab */}
          {tab === 'material' && (
            <div className="overflow-x-auto max-h-[320px]">
              <table className="w-full text-[10px]">
                <thead>
                  <tr style={{ background: 'var(--card-bg-alt)' }}>
                    {['Mark #', 'mCode', 'Section', 'Description', 'BoQ Qty', 'BoQ Wt', 'Alloc Qty', 'Alloc Wt', 'Alloc %', 'Issued', 'Balance'].map(h => (
                      <th key={h} className="p-1.5 text-left font-medium sticky top-0" style={{ color: 'var(--text-muted)', background: 'var(--card-bg-alt)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {marks.flatMap(m => (m.materialRequirements || []).map((mr, i) => (
                    <tr key={`${m.markNumber}-${i}`} style={{ borderTop: '1px solid var(--border-color)' }}>
                      <td className="p-1.5 font-mono font-semibold" style={{ color: '#F59206' }}>{m.markNumber}</td>
                      <td className="p-1.5 font-mono text-[9px]" style={{ color: 'var(--text-primary)' }}>{mr.mCode}</td>
                      <td className="p-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>{mr.sectionProfile}</td>
                      <td className="p-1.5 text-[9px]" style={{ color: 'var(--text-muted)' }}>{mr.materialDescription}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{mr.boqQty}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: 'var(--text-primary)' }}>{fmtNum(mr.boqWeight)}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: '#22c55e' }}>{mr.allocatedQty}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: '#22c55e' }}>{fmtNum(mr.allocatedWeight)}</td>
                      <td className="p-1.5 text-center"><PctBadge value={mr.allocationPercent} /></td>
                      <td className="p-1.5 text-right font-mono" style={{ color: '#143A6B' }}>{mr.issuedQty}</td>
                      <td className="p-1.5 text-right font-mono" style={{ color: mr.balanceQty > 0 ? '#f59e0b' : 'var(--text-muted)' }}>{mr.balanceQty}</td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export default function SWMSummaryDashboard({ projectId, filters = {} }) {
  const { isDark } = useTheme();
  const [summary, setSummary] = useState(null);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expandedTag, setExpandedTag] = useState(null);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.area) params.append('area', filters.area);
        if (filters.priority) params.append('priority', filters.priority);
        if (filters.workfrontStatus) params.append('workfrontStatus', filters.workfrontStatus);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const tagQs = params.toString() ? `?${params.toString()}` : '';
        const [sumRes, tagRes] = await Promise.all([
          api.get(`/v1/projects/${projectId}/swm/summary${qs}`),
          api.get(`/v1/projects/${projectId}/swm/tags${tagQs}`),
        ]);
        setSummary(sumRes.data);
        setTags(tagRes.data?.tags || []);
      } catch (err) { console.error('Failed to fetch SWM summary', err); }
      setLoading(false);
    })();
  }, [projectId, filters.area, filters.priority, filters.workfrontStatus]);

  if (loading) return (
    <div className="flex items-center justify-center py-20" data-testid="swm-summary-loading">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#F59206' }} />
    </div>
  );

  if (!summary || tags.length === 0) return (
    <div className="text-center py-20" data-testid="swm-summary-empty">
      <Package className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.5 }} />
      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No SWM Data Yet</p>
      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Go to the Data Import tab to seed demo data or import Excel files.</p>
    </div>
  );

  const c = summary.counts || {};
  const dist = summary.stateDistribution || {};
  const wts = summary.weights || {};

  // State distribution chart data
  const chartData = [
    { name: 'Unallocated', count: dist.unallocated || 0, fill: '#ef4444' },
    { name: 'Partial Soft', count: dist.partialSoft || 0, fill: '#eab308' },
    { name: 'Ready Soft', count: dist.readySoft || 0, fill: '#3b82f6' },
    { name: 'PO Ready', count: dist.poReady || 0, fill: '#143A6B' },
    { name: 'Partial Hard', count: dist.partialHard || 0, fill: '#f59e0b' },
    { name: 'Fully Allocated', count: dist.fullyAllocated || 0, fill: '#22c55e' },
  ].filter(d => d.count > 0);
  const totalTags = chartData.reduce((s, d) => s + d.count, 0);

  // Sorting
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const sortedTags = [...tags].filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return t.structuralTag?.toLowerCase().includes(s) || t.structureNameRef?.toLowerCase().includes(s) || t.area?.toLowerCase().includes(s) || t.mCode?.toLowerCase().includes(s);
  }).sort((a, b) => {
    if (!sortField) return 0;
    const dir = sortDir === 'asc' ? 1 : -1;
    const get = (row) => {
      switch (sortField) {
        case 'tag': return row.structuralTag || '';
        case 'structure': return row.structureNameRef || '';
        case 'area': return row.area || '';
        case 'type': return row.structureType || '';
        case 'priority': return row.priority ?? 999;
        case 'weight': return row.totalMTOWeight || 0;
        case 'softPct': return row.softAllocation?.allocationPercent || 0;
        case 'hardPct': return row.hardAllocation?.allocationPercent || 0;
        case 'fabPct': return row.fabricationProgress?.overallFabProgressPct || 0;
        case 'erecPct': return row.erectionProgress?.overallErectionProgressPct || 0;
        case 'status': return row.workfrontStatus || '';
        default: return '';
      }
    };
    const va = get(a), vb = get(b);
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });

  const SortHeader = ({ field, label, style, className }) => (
    <th className={`p-2 font-medium cursor-pointer select-none hover:opacity-80 ${className || ''}`}
      style={style} onClick={() => handleSort(field)} data-testid={`sort-${field}`}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {sortField === field && <span className="text-[8px] ml-0.5">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  const canExpand = (wf, markCount) => markCount > 0 || (wf && wf !== 'UNALLOCATED');

  return (
    <div className="space-y-3" data-testid="swm-summary-dashboard">
      {/* KPI Cards Row */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
        {[
          { label: 'Structures', value: c.structures || 0, icon: Layers, color: '#F59206' },
          { label: 'Tags', value: c.tags || 0, icon: Tag, color: '#3B82F6' },
          { label: 'Mark Numbers', value: c.markNumbers || 0, icon: Box, color: '#22c55e' },
          { label: 'MTO Weight', value: `${((wts.totalMTOWeight || 0) / 1000).toFixed(1)}T`, icon: Weight, color: '#F59206' },
          { label: 'Fab %', value: `${(summary.fabricationProgressPct || 0).toFixed(1)}%`, icon: Layers, color: '#143A6B' },
          { label: 'Erec %', value: `${(summary.erectionProgressPct || 0).toFixed(1)}%`, icon: Package, color: '#f59e0b' },
        ].map(card => {
          const Icon = card.icon;
          return (
            <div key={card.label} className="p-2 rounded-lg" style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
              <div className="flex items-center gap-1.5 mb-1">
                <Icon className="w-3.5 h-3.5" style={{ color: card.color }} />
                <span className="text-[9px] font-medium" style={{ color: 'var(--text-muted)' }}>{card.label}</span>
              </div>
              <p className="text-base font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{card.value}</p>
            </div>
          );
        })}
      </div>

      {/* State Distribution Bar Chart */}
      <div className="p-3 rounded-lg border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
        <p className="text-[10px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>Allocation State Distribution ({totalTags} tags)</p>
        <div style={{ height: 140 }} data-testid="state-distribution-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: -10, bottom: 4 }} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 9, fill: 'var(--text-muted)' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 11 }}
                formatter={(value) => [`${value} tags (${totalTags > 0 ? ((value / totalTags) * 100).toFixed(1) : 0}%)`, 'Count']} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50}>
                {chartData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search tag, structure, area, mCode..."
          data-testid="swm-summary-search"
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border" style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
      </div>

      {/* Flat Matrix Table */}
      <div className="rounded-lg border overflow-x-auto" style={{ borderColor: 'var(--border-color)' }}>
        <table className="w-full text-[11px]" data-testid="swm-summary-table">
          <thead>
            <tr style={{ background: 'var(--card-bg-alt)', position: 'sticky', top: 0, zIndex: 2 }}>
              <th className="p-2 w-8" />
              <SortHeader field="tag" label="Tag #" style={{ color: 'var(--text-muted)', minWidth: 80 }} className="text-left" />
              <SortHeader field="structure" label="Structure" style={{ color: 'var(--text-muted)' }} className="text-left" />
              <SortHeader field="area" label="Area" style={{ color: 'var(--text-muted)' }} className="text-left" />
              <SortHeader field="type" label="Type" style={{ color: 'var(--text-muted)' }} className="text-left" />
              <SortHeader field="priority" label="P" style={{ color: 'var(--text-muted)' }} className="text-center w-8" />
              <th className="p-2 text-left font-medium" style={{ color: '#F59206' }}>mCode</th>
              <th className="p-2 text-left font-medium" style={{ color: 'var(--text-muted)' }}>Section</th>
              <SortHeader field="weight" label="MTO Wt (kg)" style={{ color: 'var(--text-muted)' }} className="text-right" />
              <SortHeader field="softPct" label="Soft %" style={{ color: '#3b82f6', borderLeft: '2px solid var(--border-color)' }} className="text-center" />
              <SortHeader field="hardPct" label="Hard %" style={{ color: '#22c55e' }} className="text-center" />
              <SortHeader field="fabPct" label="Fab %" style={{ color: '#143A6B' }} className="text-center" />
              <SortHeader field="erecPct" label="Erec %" style={{ color: '#f59e0b' }} className="text-center" />
              <SortHeader field="status" label="Status" style={{ color: 'var(--text-muted)' }} className="text-center" />
              <th className="p-2 text-center font-medium" style={{ color: 'var(--text-muted)', borderLeft: '2px solid var(--border-color)' }}>Marks</th>
            </tr>
          </thead>
          <tbody>
            {sortedTags.map((tag, idx) => {
              const wf = tag.workfrontStatus || 'UNALLOCATED';
              const sc = STATUS_COLORS[wf] || STATUS_COLORS.UNALLOCATED;
              const softPct = tag.softAllocation?.allocationPercent || 0;
              const hardPct = tag.hardAllocation?.allocationPercent || 0;
              const fabPct = tag.fabricationProgress?.overallFabProgressPct || 0;
              const erecPct = tag.erectionProgress?.overallErectionProgressPct || 0;
              const expandable = canExpand(wf, tag.markNumbersCount || tag.markCount || 0);
              const isExpanded = expandedTag === tag.structuralTag;
              const stripe = idx % 2 === 0 ? 'var(--card-bg)' : 'var(--card-bg-alt)';

              return (
                <Fragment key={tag.structuralTag}>
                  <tr style={{ borderTop: '1px solid var(--border-color)', background: isExpanded ? '#F5920608' : stripe }}
                    data-testid={`wf-tag-${tag.structuralTag}`}
                    className={expandable ? 'cursor-pointer' : ''}
                    onClick={expandable ? () => setExpandedTag(isExpanded ? null : tag.structuralTag) : undefined}>
                    <td className="p-2">
                      {expandable ? (isExpanded
                        ? <ChevronDown className="w-3.5 h-3.5" style={{ color: '#F59206' }} />
                        : <ChevronRight className="w-3.5 h-3.5" style={{ color: '#22c55e' }} />
                      ) : null}
                    </td>
                    <td className="p-2 font-mono text-[10px] font-semibold" style={{ color: '#F59206' }}>{tag.structuralTag}</td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{tag.structureNameRef}</td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{tag.area}</td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{tag.structureType}</td>
                    <td className="p-2 text-center">
                      {tag.priority ? <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][tag.priority]}20`, color: ['', '#ef4444', '#f59e0b', '#3b82f6', '#143A6B', '#94a3b8'][tag.priority] }}>P{tag.priority}</span> : <span style={{ color: 'var(--text-muted)' }}>-</span>}
                    </td>
                    <td className="p-2 font-mono text-[10px]" style={{ color: '#F59206' }}>{tag.mCode}</td>
                    <td className="p-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>{tag.section}</td>
                    <td className="p-2 text-right font-mono text-[10px]" style={{ color: 'var(--text-primary)' }}>{fmtNum(tag.totalMTOWeight)}</td>
                    <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}><MiniBar value={softPct} color="#3b82f6" /></td>
                    <td className="p-2 text-center"><MiniBar value={hardPct} color="#22c55e" /></td>
                    <td className="p-2 text-center"><MiniBar value={fabPct} color="#143A6B" /></td>
                    <td className="p-2 text-center"><MiniBar value={erecPct} color="#f59e0b" /></td>
                    <td className="p-2 text-center"><span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ background: sc.bg, color: sc.text }}>{sc.label}</span></td>
                    <td className="p-2 text-center" style={{ borderLeft: '2px solid var(--border-color)' }}>
                      {expandable && (
                        <button onClick={e => { e.stopPropagation(); setExpandedTag(isExpanded ? null : tag.structuralTag); }}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-medium transition-colors hover:opacity-80"
                          style={{ background: '#F5920615', color: '#F59206', border: '1px solid #F5920630' }}
                          data-testid={`ref-marks-${tag.structuralTag}`}>
                          <Box className="w-2.5 h-2.5" /> View
                        </button>
                      )}
                    </td>
                  </tr>
                  {isExpanded && <MarkDetailPanel projectId={projectId} structuralTag={tag.structuralTag} onClose={() => setExpandedTag(null)} />}
                </Fragment>
              );
            })}
            {sortedTags.length === 0 && <tr><td colSpan={16} className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>No tags found. Seed data from Data Import tab.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MiniBar({ value, color }) {
  const v = Math.min(100, Math.max(0, value || 0));
  return (
    <div className="flex items-center gap-1 justify-center">
      <div className="w-10 h-1.5 rounded-full overflow-hidden" style={{ background: `${color}15` }}>
        <div className="h-full rounded-full" style={{ width: `${v}%`, background: color }} />
      </div>
      <span className="text-[9px] font-mono font-bold" style={{ color }}>{v.toFixed(0)}%</span>
    </div>
  );
}

function PctBadge({ value }) {
  const v = value || 0;
  return (
    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{
      background: v >= 100 ? '#22c55e20' : v > 0 ? '#f59e0b20' : '#94a3b820',
      color: v >= 100 ? '#22c55e' : v > 0 ? '#f59e0b' : '#94a3b8',
    }}>{fmtPct(v)}</span>
  );
}
